import {
  Filtering,
  Pagination,
  QBox,
  QMultiSelect,
  QSelectItem,
  QSelectPlaceholder,
  QText,
} from '@qualio/ui-components';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { QualioDocument } from '../../../api/model/document';
import { Tag } from '../../../api/model/tag';
import { WorkspaceFilteringDefinition } from '../../../util/WorkspaceUtils';
import styles from './DocumentLibrary.module.css';

type TagsFilterProps = {
  isLoadingTags: boolean;
  fetchedTags: Tag[];
  setSelectedDocumentsMap?: (selected: Map<number, QualioDocument>) => void;
};

const getTagsItems = (fetchedTags: Tag[]): QSelectItem[] => {
  return fetchedTags.map((tag) => ({
    label: tag.name,
    value: String(tag.id),
    ...(tag.group_ids.length > 0 && {
      rightIcon: { iconName: 'Lock', 'data-cy': `tag-lock-icon-${tag.id}` },
    }),
  }));
};

export const TagsFilter = ({
  fetchedTags,
  isLoadingTags,
  setSelectedDocumentsMap,
}: TagsFilterProps) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const filtering = Filtering.useFiltering<WorkspaceFilteringDefinition>();
  const pagination = Pagination.usePaginationMaybe();
  const tagItems = getTagsItems(fetchedTags);
  const tagsSearchParam = searchParams.get('filteringTag');
  const tagsParams = filtering.filters?.filteringTag?.value;
  const selectedTags = (tagsParams ?? []).filter(
    (tag): tag is string => tag !== null,
  );

  useEffect(() => {
    pagination?.setPageIndex(0);
    // Can't have `paginaton` as a dependency or else pagination will not work
    // eslint-disable-next-line
  }, [tagsSearchParam]);

  const onTagFilterChange = (tagItems: readonly QSelectItem[]) => {
    const tagIds = tagItems.map((tagItem) => tagItem.value);
    filtering.setFilters({
      filteringTag: tagIds,
    });
    // reset selection in workspace
    if (setSelectedDocumentsMap) {
      setSelectedDocumentsMap(new Map());
    }
  };

  const filterTagsBySearchTerm = (option: QSelectItem, searchTerm: string) => {
    if (!searchTerm) {
      return true;
    }

    return option.label.toLowerCase().includes(searchTerm.toLowerCase());
  };

  return (
    <QBox
      className={`${styles['document-library__tag-multiselect']}`}
      data-cy="tags-filter"
    >
      <QMultiSelect
        isLoading={isLoadingTags}
        size="sm"
        options={tagItems}
        onChange={onTagFilterChange}
        value={selectedTags}
        inputId="tags-filter-input"
        filterOption={({ data: option }, searchTerm) =>
          filterTagsBySearchTerm(option, searchTerm)
        }
      >
        <QSelectPlaceholder>
          <QText fontSize="sm" data-cy="library-tag-filter">
            {t('forms.filterByTags')}
          </QText>
        </QSelectPlaceholder>
      </QMultiSelect>
    </QBox>
  );
};
